import { LOCAL_STORAGE_TYPES, APP_ROUTE, ONBOARDING } from '@/constants';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ClientContainer, UserContainer } from '@/providers';

import useLocalStorage from '@/hooks/localstorage';
import { useChangeBusiness } from '@/hooks/api/business';
import { useSetPermissions } from '@/hooks/api/user';
import { Navigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

interface Permission {
  clientId: string;
  businessId: string;
}

interface Business {
  id: string;
  name: string;
  logo?: string;
  access: {
    isPrimary: boolean;
  };
  clients: Array<{
    id: string;
  }>;
}

interface WorkspaceStorage {
  businessId: string;
}

export function SwitchBusinessModal() {
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const { get } = useLocalStorage();

  const [showSwitchBusinessModal, setShowSwitchBusinessModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeBusiness = useChangeBusiness();
  const setCurrentBusinessPermission = useSetPermissions();
  const { onboarding: onboardingStatus } = userState?.user ?? {};

  const hasWorkspaceAccessWithCurrentBusiness = (
    clientId?: string,
    businessId?: string
  ): boolean => {
    if (!clientId || !businessId || !userState?.permissions) return false;

    return userState.permissions.some((permission: Permission) => {
      return (
        permission.clientId === clientId && permission.businessId === businessId
      );
    });
  };

  const getBusinessWithClient = (userData: any): Business | null => {
    if (!userData?.businessOptions) return null;

    const urlClientId = window.location.pathname.split('/')[2];
    if (!urlClientId) return null;

    return (
      userData.businessOptions.find((business: Business) =>
        business.clients?.some(client => String(client.id) === urlClientId)
      ) || null
    );
  };

  const handleBusinessSwitch = async (business: Business) => {
    try {
      setError(null);
      setCurrentBusinessPermission(business.id);
      await changeBusiness(business);
      window.location.reload();
    } catch (errorState) {
      console.error('Error switching business:', errorState);
      setError('Failed to switch business. Please try again.');
    }
  };

  const hasValidClientInUrl = useCallback(() => {
    const urlClientId = window.location.pathname.split('/')[2];
    if (!urlClientId || !userState?.businessOptions) return false;

    return userState.businessOptions.some((business: Business) =>
      business.clients?.some(client => String(client.id) === urlClientId)
    );
  }, [userState?.businessOptions]);

  useEffect(() => {
    const businessForCurrentClient = getBusinessWithClient(userState);
    if (
      businessForCurrentClient &&
      String(userState?.business?.id) !== String(businessForCurrentClient.id)
    ) {
      setShowSwitchBusinessModal(true);
    }
  }, [userState?.businessOptions, clientState?.selectedClient?.id]);

  // Handle routing cases
  if (window.location.pathname === `/${APP_ROUTE.ONBOARDING}`) {
    return null;
  }

  if (onboardingStatus !== ONBOARDING.COMPLETE_STEP) {
    return <Navigate to={`/${APP_ROUTE.ONBOARDING}`} />;
  }

  if (!hasValidClientInUrl()) {
    return (
      <Navigate
        to={`/ws/${clientState?.selectedClient?.id}/${APP_ROUTE.HOME}`}
      />
    );
  }

  const currentBusiness = getBusinessWithClient(userState);
  const workspaceStorage = get(
    LOCAL_STORAGE_TYPES.WORKSPACE
  ) as WorkspaceStorage | null;

  return (
    <Dialog open={showSwitchBusinessModal}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Switch Business</DialogTitle>
        </DialogHeader>

        {error && <p className="text-red-600 mb-4">{error}</p>}

        {!hasWorkspaceAccessWithCurrentBusiness(
          clientState?.selectedClient?.id,
          userState?.business?.id
        ) && (
          <>
            <p className="text-gray-600 mb-4">
              You need to switch business first to access this workspace
            </p>
            <div className="grid gap-4">
              {currentBusiness ? (
                <div
                  key={currentBusiness.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:border-primary transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {currentBusiness.logo ? (
                      <img
                        src={currentBusiness.logo}
                        alt={currentBusiness.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-lg font-medium text-gray-600">
                          {currentBusiness.name.charAt(0)}
                        </span>
                      </div>
                    )}

                    <div>
                      <h3 className="font-medium">
                        {currentBusiness.name}
                        {currentBusiness.access?.isPrimary && (
                          <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                            Primary
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {currentBusiness.clients?.length ?? 0} workspace
                        {(currentBusiness.clients?.length ?? 0) !== 1
                          ? 's'
                          : ''}
                      </p>
                    </div>
                  </div>

                  <Button
                    intent="secondary"
                    disabled={
                      workspaceStorage?.businessId === currentBusiness.id
                    }
                    onClick={() => {
                      if (workspaceStorage?.businessId !== currentBusiness.id) {
                        handleBusinessSwitch(currentBusiness);
                      }
                    }}
                  >
                    {workspaceStorage?.businessId === currentBusiness.id
                      ? 'Current Business'
                      : 'Switch'}
                  </Button>
                </div>
              ) : (
                <p className="text-gray-600">
                  No business available to switch to.
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
